// MA-03b — private-beta access gate hook.
//
// Checks whether the signed-in user's email appears in access_allowlist.
// Called unconditionally from App.jsx (rules of hooks) after useAuth returns
// a non-null user, BEFORE any child-PII or onboarding operations.
//
// States:
//   loading: true  → query in flight; App.jsx adds this to the combined
//                     loading guard so no post-auth content renders yet.
//   allowed: true  → user is on the list; gate passes.
//   allowed: false → not on the list; App.jsx renders PrivateBetaScreen.
//
// Dev/e2e bypass: if isDevBypass() is true (the ?dev=1 URL param, compiled
// out of production builds), the gate short-circuits to allowed=true. This
// keeps all e2e tests and local dev sessions working without a real DB row.
//
// Error handling: any query error (network, RLS misconfiguration, timeout)
// is FAIL-CLOSED — treated as "not allowed" and surfaced as console.error
// so the developer can diagnose without seeing raw Supabase errors.
//
// MA-271: fail-closed is right for a NEVER-approved user, but wrong for a
// returning parent whose account was previously confirmed allowlisted —
// that outage should hold them at a recoverable retry surface instead of
// the "you were never approved" screen. See allowlistOutageGuard.js for the
// asymmetric-wrong-guess decision this hook consults on every error.
//
// The pure `checkAllowlist(email, client)` is exported separately for unit
// testing (mirrors the pattern used by onboardingHoldGuard.js).
//
// MA-935 (D-TUX-23): a second way past the gate. After the access_allowlist
// read (always first, so nothing below can ever block an allowlisted user),
// the hook reads the user's OWN claim_code_admissions row (DRAFT migration
// 0056; RLS returns only rows whose user_id is the caller). Either one
// allows. The admission read fails closed: any error means "not admitted".
// A missing table or function (42P01, PGRST205, PGRST202 — the pre-apply
// state) is a clean "not admitted", so the gate behaves exactly as today. Any
// other admission-read error is treated like an allowlist error for the
// MA-271 outage hold (a previously confirmed user is held at the retry
// surface; everyone else sees PrivateBetaScreen). `checkAccess` is the pure
// decision the hook runs; it is exported for the executed tests.

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../supabaseClient.js";
import { isDevBypass } from "../lib/persistence.ts";
import { recordAllowlistCheckResult, shouldHoldForAllowlistOutage } from "./allowlistOutageGuard.js";

/**
 * Pure function — exported for unit testing.
 * Queries access_allowlist for a single email; returns { allowed, error }.
 *
 * @param {string} email
 * @param {{ from: (table: string) => any }} client - Supabase-shaped client
 * @returns {Promise<{ allowed: boolean, error?: unknown }>}
 */
export async function checkAllowlist(email, client) {
  try {
    const { data, error } = await client
      .from("access_allowlist")
      .select("email")
      .eq("email", email)
      .maybeSingle();
    if (error) return { allowed: false, error };
    return { allowed: data !== null };
  } catch (err) {
    return { allowed: false, error: err };
  }
}

/** PostgREST / Postgres answers that mean "migration 0056 is not applied":
 *  42P01 (relation does not exist), PGRST205 (table not in the schema
 *  cache), PGRST202 (function not in the schema cache). */
export const ADMISSION_NOT_APPLIED_CODES = Object.freeze(["42P01", "PGRST205", "PGRST202"]);

/**
 * Pure function — exported for unit testing (MA-935).
 * Reads the caller's own claim_code_admissions row; returns
 * { admitted, error?, notApplied? }. Never throws; any error is "not admitted".
 *
 * @param {string|undefined|null} userId
 * @param {{ from: (table: string) => any }} client
 * @returns {Promise<{ admitted: boolean, error?: unknown, notApplied?: boolean }>}
 */
export async function checkAdmission(userId, client) {
  if (!userId) return { admitted: false };
  try {
    const { data, error } = await client
      .from("claim_code_admissions")
      .select("user_id")
      .eq("user_id", userId)
      .limit(1)
      .maybeSingle();
    if (error) {
      const notApplied = ADMISSION_NOT_APPLIED_CODES.includes(error?.code);
      return { admitted: false, error, notApplied };
    }
    return { admitted: data !== null && data !== undefined && data.user_id === userId };
  } catch (err) {
    return { admitted: false, error: err };
  }
}

/**
 * Pure decision — exported for unit testing (MA-935). The allowlist row is
 * read FIRST; the admission row is read only when the allowlist did not
 * allow. Returns { allowed, error }:
 *   - allowed: either read found the user's row;
 *   - error: the error that should drive the MA-271 outage hold (the
 *     allowlist error, else a non-"not applied" admission error), or null.
 *     With error null the outcome came from clean reads and the hook records
 *     it as today.
 *
 * @param {string} email
 * @param {string|undefined|null} userId
 * @param {{ from: (table: string) => any }} client
 */
export async function checkAccess(email, userId, client) {
  const listed = await checkAllowlist(email, client);
  if (listed.allowed) return { allowed: true, error: null };
  const admission = await checkAdmission(userId, client);
  if (admission.admitted) return { allowed: true, error: null };
  if (listed.error) return { allowed: false, error: listed.error };
  if (admission.error && !admission.notApplied) return { allowed: false, error: admission.error };
  return { allowed: false, error: null };
}

/**
 * Hook — called unconditionally from App.jsx.
 *
 * @param {{ id?: string, email?: string } | null} user
 * @returns {{ loading: boolean, allowed: boolean, outage: boolean, error: unknown, retry: () => Promise<void> }}
 */
export default function useAllowlist(user) {
  const [state, setState] = useState({ loading: true, allowed: false, outage: false, error: null });
  const aliveRef = useRef(true);
  // Stable primitives, not the user object reference — useAuth's
  // onAuthStateChange fires a fresh `user` object on every auth event
  // (including periodic TOKEN_REFRESHED for the SAME account), so runCheck
  // must depend on these, not on `user` itself, or a future edit that adds
  // it to the mount effect's deps would re-query on every token refresh.
  const userId = user?.id;
  const userEmail = user?.email;

  const runCheck = useCallback(async () => {
    // No user yet — gate is moot; clear loading so the !user branch handles it.
    if (!userId) {
      if (aliveRef.current) setState({ loading: false, allowed: false, outage: false, error: null });
      return;
    }

    // Dev/e2e bypass: ?dev=1 users are auto-allowed (no real DB in local dev).
    if (isDevBypass()) {
      if (aliveRef.current) setState({ loading: false, allowed: true, outage: false, error: null });
      return;
    }

    if (aliveRef.current) setState((s) => ({ ...s, loading: true }));
    const result = await checkAccess(userEmail ?? "", userId, supabase);
    if (!aliveRef.current) return;

    if (result.error) {
      console.error("[useAllowlist] access check failed (fail-closed):", result.error);
      setState({
        loading: false,
        allowed: false,
        outage: shouldHoldForAllowlistOutage({ error: result.error, userId }),
        error: result.error,
      });
      return;
    }

    // Clean (error-free) result — sync the "was this user ever confirmed
    // allowed" signal so a FUTURE outage can tell returning-approved apart
    // from never-approved. Clears on a clean allowed:false too, so a
    // revoked account never carries a stale confirmed-flag into a later,
    // unrelated outage.
    recordAllowlistCheckResult(userId, result.allowed);
    setState({ loading: false, allowed: result.allowed, outage: false, error: null });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, userEmail]);

  useEffect(() => {
    aliveRef.current = true;
    runCheck();
    return () => { aliveRef.current = false; };
  // Depend on user.id (stable identity) not the object reference, and not
  // on runCheck itself (it's already keyed on the same stable primitives).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  return { ...state, retry: runCheck };
}
